import { getStringFormatLabel } from '@hap/utils'
import * as LabelPrimitive from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'
import { AnimatePresence, m } from 'motion/react'
import * as React from 'react'
import { FocusEventHandler, ReactNode } from 'react'
import {
  Control,
  Controller,
  ControllerProps,
  FieldError,
  FieldPath,
  FieldPathValue,
  FieldValues,
  FormProvider,
  Path,
  useFormContext,
} from 'react-hook-form'
import PhoneInputComponent from 'react-phone-number-input/input'

import { cn } from '../utils'
import { Button } from './button'
import { Checkbox } from './checkbox'
import { Input, inputStyles } from './input'
import { Label } from './label'
import { Multiline } from './multiline'
import MultiValue from './multivalue'
import { RadioGroup, RadioGroupItem } from './radio-group'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { Slider } from './slider'
import { Switch } from './switch'
import { toast } from './toast'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
  required?: boolean
  disabled?: boolean
  readonly?: boolean
}

type FieldFormProps<V extends FieldValues> = {
  label?: string
  name: Path<V>
  type?: string
  required?: boolean
  readonly?: boolean
  autoFocus?: boolean
  control: Control<V>
  placeholder?: string
  disabled?: boolean
  format?: (value: string) => string
  formatOnBlur?: (value: string) => string
  clearable?: boolean | string
  className?: string
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName> & {
  required?: boolean
  disabled?: boolean
  readonly?: boolean
}) => {
  return (
    <FormFieldContext.Provider
      value={{
        name: props.name,
        required: props.required,
        disabled: props.disabled,
        readonly: props.readonly,
      }}
    >
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

export const FormFieldInputLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { formItemId, required } = useFormField()
  return (
    <Label
      ref={ref}
      className={cn('pointer-events-none', className)}
      required={required}
      htmlFor={formItemId}
      {...props}
    />
  )
})
FormFieldInputLabel.displayName = 'FormFieldInputLabel'

function FormFieldInput<V extends FieldValues = FieldValues>(
  props: FieldFormProps<V> & {
    suffix?: ReactNode
    prefix?: ReactNode
    minLength?: number
    maxLength?: number
    footnote?: string
    variant?: 'default' | 'ghost'
    size?: 'lg'
    inputRef?: React.Ref<HTMLInputElement>
    inputClassName?: string
    autoComplete?: React.InputHTMLAttributes<HTMLInputElement>['autoComplete']
  },
) {
  return (
    <FormField
      control={props.control}
      name={props.name}
      required={props.required}
      disabled={props.disabled}
      render={({ field }) => {
        return (
          <FormItem className={props.className}>
            <div className="group relative flex flex-col gap-3">
              <FormFieldInputLabel>{props.label}</FormFieldInputLabel>
              <div className="relative">
                <div
                  className={cn(
                    'text-input-secondary-foreground absolute top-1/2 left-2 -translate-y-1/2 text-lg',
                    { 'opacity-50': props.disabled },
                  )}
                >
                  {props.prefix}
                </div>
                <FormControl>
                  <Input
                    {...field}
                    type={props.type}
                    placeholder={props.placeholder}
                    autoFocus={props.autoFocus}
                    disabled={props.disabled}
                    minLength={props.minLength}
                    maxLength={props.maxLength}
                    size={props.size}
                    variant={props.variant}
                    ref={props.inputRef}
                    className={props.inputClassName}
                    autoComplete={props.autoComplete}
                    onChange={(e) =>
                      field.onChange(
                        props.format ? props.format(e.currentTarget.value) : e,
                      )
                    }
                    onBlur={(e) => {
                      if (props.formatOnBlur) {
                        field.onChange(
                          props.formatOnBlur(e.currentTarget.value),
                        )
                      }
                    }}
                  />
                </FormControl>
                <div
                  className={cn(
                    'text-input-secondary-foreground/60 absolute top-1/2 right-2 -translate-y-1/2',
                    { 'opacity-50': props.disabled },
                  )}
                >
                  {props.suffix}
                </div>
              </div>
            </div>
            <FormMessage footnote={props.footnote} disabled={props.disabled} />
          </FormItem>
        )
      }}
    />
  )
}

FormFieldInput.displayName = 'FormFieldInput'

function FormFieldDuration<V extends FieldValues = FieldValues>(
  props: FieldFormProps<V> & {
    footnote?: string
    size?: 'lg'
  },
) {
  return (
    <FormField
      control={props.control}
      name={props.name}
      required={props.required}
      disabled={props.disabled}
      render={({ field }) => {
        return (
          <FormItem className={props.className}>
            <div className="group relative flex flex-col gap-2">
              <FormFieldInputLabel>{props.label}</FormFieldInputLabel>
              <div className="flex items-center">
                <Clearable
                  hasValue={field.value !== '' && field.value !== undefined}
                  onClick={() => field.onChange('')}
                  clearable={props.clearable}
                />
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      type={props.type}
                      placeholder={props.placeholder}
                      autoFocus={props.autoFocus}
                      disabled={props.disabled}
                      size={props.size}
                      onChange={(e) =>
                        field.onChange(
                          e.currentTarget.value.replace(/[^0-9:]/g, ''),
                        )
                      }
                    />
                  </FormControl>
                  <div
                    className={cn(
                      'text-input-secondary-foreground absolute top-1/2 right-2 -translate-y-1/2',
                      { 'opacity-50': props.disabled },
                    )}
                  >
                    {getStringFormatLabel(field.value) || 'Seconds'}
                  </div>
                </div>
              </div>
            </div>
            <FormMessage footnote={props.footnote} disabled={props.disabled} />
          </FormItem>
        )
      }}
    />
  )
}

function FormFieldPercent<V extends FieldValues = FieldValues>(
  props: FieldFormProps<V> & {
    footnote?: string
    size?: 'lg'
  },
) {
  return (
    <FormField
      control={props.control}
      name={props.name}
      required={props.required}
      disabled={props.disabled}
      render={({ field }) => {
        return (
          <FormItem className={props.className}>
            <div className="group relative flex flex-col gap-2">
              <FormFieldInputLabel>{props.label}</FormFieldInputLabel>
              <div className="flex items-center">
                <Clearable
                  hasValue={field.value !== '' && field.value !== undefined}
                  onClick={() => field.onChange('')}
                  clearable={props.clearable}
                />
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      type={props.type}
                      placeholder={props.placeholder}
                      autoFocus={props.autoFocus}
                      disabled={props.disabled}
                      size={props.size}
                      onChange={(e) =>
                        field.onChange(
                          e.currentTarget.value.replace(/[^0-9:]/g, ''),
                        )
                      }
                    />
                  </FormControl>
                  <div
                    className={cn(
                      'text-input-secondary-foreground absolute top-1/2 right-2 -translate-y-1/2',
                      { 'opacity-50': props.disabled },
                    )}
                  >
                    %
                  </div>
                </div>
              </div>
            </div>
            <FormMessage footnote={props.footnote} disabled={props.disabled} />
          </FormItem>
        )
      }}
    />
  )
}

function Clearable(props: {
  hasValue: boolean
  clearable?: boolean | string
  onClick: () => void
}) {
  return props.clearable ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          className={cn('mr-4', {
            'pointer-events-none opacity-0': !props.hasValue,
          })}
          variant="ghost"
          size="icon"
          icon="arrow-counter-clockwise"
          aria-label={
            typeof props.clearable === 'string' ? props.clearable : 'Clear'
          }
          onClick={props.onClick}
          disabled={!props.hasValue}
          tabIndex={props.hasValue ? 0 : -1}
          aria-hidden={!props.hasValue}
        ></Button>
      </TooltipTrigger>
      <TooltipContent>
        {typeof props.clearable === 'string' ? props.clearable : 'Clear'}
      </TooltipContent>
    </Tooltip>
  ) : null
}

function FormFieldSlider<V extends FieldValues = FieldValues>(
  props: Omit<FieldFormProps<V>, 'placeholder'> & {
    max: number
    step?: number
    suffix?: ReactNode
    hideInput?: boolean
    placeholder?: number
  },
) {
  return (
    <FormField
      control={props.control}
      name={props.name}
      required={props.required}
      disabled={props.disabled}
      render={({ field }) => {
        return (
          <FormItem>
            <div className="group relative flex items-center gap-3">
              <div className="relative">
                <FormFieldInputLabel>{props.label}</FormFieldInputLabel>
                {!props.hideInput && (
                  <FormControl>
                    <Input
                      {...field}
                      name={field.name}
                      onChange={(e) => {
                        const value = parseFloat(e.currentTarget.value)
                        field.onChange(Number.isNaN(value) ? '' : value)
                      }}
                      type="number"
                      placeholder={`${props.placeholder}`}
                      className="w-[80px]"
                    />
                  </FormControl>
                )}
                <div className="absolute top-1/2 right-2 -translate-y-1/2 select-none">
                  {props.suffix}
                </div>
              </div>

              <Slider
                value={[field.value ?? props.placeholder ?? 0]}
                max={props.max}
                step={props.step || 1}
                onValueChange={(value) => field.onChange(value[0])}
              />

              <Clearable
                hasValue={field.value}
                onClick={() => field.onChange('')}
                clearable={props.clearable}
              />
            </div>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

function FormFieldPhoneInput<V extends FieldValues = FieldValues>(
  props: FieldFormProps<V>,
) {
  return (
    <FormField
      control={props.control}
      name={props.name}
      required={props.required}
      render={({ field }) => {
        return (
          <FormItem>
            <FormFieldInputLabel>{props.label}</FormFieldInputLabel>
            <div className="group relative grid h-12">
              <FormControl>
                <PhoneInputComponent
                  defaultCountry="US"
                  value={field.value}
                  onChange={field.onChange}
                  className={cn(inputStyles)}
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

FormFieldPhoneInput.displayName = 'FormFieldPhoneInput'

const FormFieldMultiline = <V extends FieldValues = FieldValues>(
  props: FieldFormProps<V> & {
    onBlur?: FocusEventHandler<HTMLTextAreaElement>
    rows?: number
    minRows?: number
    maxRows?: number
    footnote?: string
    variant?: 'default' | 'ghost'
  },
) => {
  return (
    <FormField
      control={props.control}
      name={props.name}
      required={props.required}
      render={({ field }) => {
        return (
          <FormItem>
            <div className="group relative flex flex-col gap-2">
              <FormFieldInputLabel>{props.label}</FormFieldInputLabel>
              <FormControl>
                <Multiline
                  {...field}
                  placeholder={props.placeholder}
                  disabled={props.disabled}
                  onBlur={props.onBlur}
                  rows={props.rows}
                  maxRows={props.maxRows}
                  minRows={props.minRows}
                  variant={props.variant}
                  autoFocus={props.autoFocus}
                  ref={(e) => {
                    field.ref(e)
                  }}
                />
              </FormControl>
            </div>
            <FormMessage footnote={props.footnote} disabled={props.disabled} />
          </FormItem>
        )
      }}
    />
  )
}

FormFieldMultiline.displayName = 'FormFieldMultiline'

const FormFieldMultivalue = <V extends FieldValues = FieldValues>(
  props: FieldFormProps<V> & {
    placeholder?: string
    validator?: (v: string[]) => string[]
  },
) => {
  return (
    <FormField
      control={props.control}
      name={props.name}
      required={props.required}
      defaultValue={[] as FieldPathValue<V, Path<V>>}
      render={({ field, fieldState }) => {
        return (
          <FormItem>
            <FormFieldInputLabel>{props.label}</FormFieldInputLabel>
            <div className="group relative grid">
              <FormControl>
                <MultiValue
                  placeholder={props.placeholder}
                  error={fieldState.error}
                  {...field}
                  validator={props.validator}
                  label={props.label}
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

FormFieldMultivalue.displayName = 'FormFieldMultivalue'

const FormFieldSelect = <V extends FieldValues = FieldValues>(
  props: FieldFormProps<V> & {
    defaultValue?: FieldPathValue<V, Path<V>>
    footnote?: string
    options: {
      label: ReactNode
      value: string
      suffix?: ReactNode
      description?: ReactNode
    }[]
  },
) => {
  return (
    <FormField
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      disabled={props.disabled}
      readonly={props.readonly}
      render={({ field }) => {
        return (
          <FormItem>
            <Select
              value={field.value}
              onValueChange={field.onChange}
              key={field.value ? `status-${field.value}` : 'status-initial'}
              disabled={field.disabled || props.readonly}
            >
              <div className="group relative flex flex-col gap-2">
                <FormFieldInputLabel>{props.label}</FormFieldInputLabel>
                <FormControl>
                  <SelectTrigger disabled={field.disabled}>
                    <SelectValue placeholder={props.placeholder} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectGroup>
                    {props.options.map((opt) => (
                      <SelectItem
                        value={opt.value}
                        key={opt.value}
                        description={opt.description}
                        suffix={opt.suffix}
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </div>
            </Select>
            <FormMessage disabled={props.disabled} footnote={props.footnote} />
          </FormItem>
        )
      }}
    />
  )
}

FormFieldSelect.displayName = 'FormFieldSelect'

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState, formState } = useFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>')
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    required: fieldContext.required,
    ...fieldState,
  }
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
)

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const id = React.useId()

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn('space-y-2', className)} {...props} />
    </FormItemContext.Provider>
  )
})
FormItem.displayName = 'FormItem'

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField()

  return (
    <Label
      ref={ref}
      className={cn(error && 'text-warn', className)}
      htmlFor={formItemId}
      {...props}
    />
  )
})
FormLabel.displayName = 'FormLabel'

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  )
})
FormControl.displayName = 'FormControl'

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField()

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
})
FormDescription.displayName = 'FormDescription'

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement> & {
    footnote?: string
    disabled?: boolean
  }
>(({ className, children, footnote, disabled, ...props }, ref) => {
  const { error, formMessageId } = useFormField()
  const specificError = error
    ? (Object.values(error).find(
        (value) => typeof value === 'object' && 'message' in value,
      ) as FieldError)
    : null

  const body = specificError
    ? specificError.message
    : error
      ? String(error?.message)
      : footnote || children

  if (!body) {
    return null
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn(
        'text-accent-foreground text-sm',
        {
          'text-input-invalid font-semibold': !!specificError || !!error,
          'opacity-50': disabled,
        },
        className,
      )}
      {...props}
    >
      {body}
    </p>
  )
})
FormMessage.displayName = 'FormMessage'

type CheckboxFieldProps<V extends FieldValues = FieldValues> = {
  layout?: { columns?: number | 'auto' }
  options: Array<{ label: ReactNode; value: string; description?: string }>
  footnote?: string
} & FieldFormProps<V>

function FormFieldCheckboxGroup<V extends FieldValues = FieldValues>({
  layout = { columns: 1 },
  ...props
}: CheckboxFieldProps<V>) {
  return (
    <FormField
      control={props.control}
      name={props.name}
      required={props.required}
      disabled={props.disabled}
      render={() => {
        return (
          <FormItem>
            <Label required={props.required}>{props.label}</Label>
            <FormControl className="grid items-stretch">
              <div
                style={{
                  gridTemplateColumns:
                    typeof layout.columns === 'number'
                      ? `repeat(${layout.columns}, 1fr)`
                      : undefined,
                  display:
                    layout.columns === 'auto' ? 'inline-grid' : undefined,
                }}
                className={cn('gap-2', {
                  'grid-inline grid-flow-col': layout.columns === 'auto',
                  grid: layout.columns !== 'auto',
                })}
              >
                {props.options.map((opt) => (
                  <FormField
                    key={opt.value}
                    control={props.control}
                    name={props.name}
                    disabled={props.disabled}
                    render={({ field }) => {
                      const checked = field.value?.includes(opt.value)
                      return (
                        <FormItem key={opt.value}>
                          <label
                            className={cn(
                              'bg-input-secondary-background hover:bg-input-background text-input-foreground flex min-h-[39px] cursor-pointer items-center space-y-0 space-x-3 rounded-md px-3 py-3',
                              {
                                'bg-input-background': checked,
                                'hover:bg-input-secondary-background bg-input-background/50 cursor-not-allowed':
                                  field.disabled,
                              },
                            )}
                          >
                            <FormControl>
                              <Checkbox
                                checked={checked}
                                value={checked}
                                disabled={field.disabled}
                                onCheckedChange={(checked: boolean) => {
                                  return checked
                                    ? field.onChange([
                                        ...(field.value || []),
                                        opt.value,
                                      ])
                                    : field.onChange(
                                        field.value.filter(
                                          (value: string) =>
                                            value !== opt.value,
                                        ),
                                      )
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-md text-input-foreground flex-1 cursor-pointer font-normal">
                              {opt.label}
                            </FormLabel>
                            {!!opt.description && (
                              <FormLabel className="text-input-secondary-foreground cursor-pointer text-xs font-normal">
                                {opt.description}
                              </FormLabel>
                            )}
                          </label>
                        </FormItem>
                      )
                    }}
                  />
                ))}
              </div>
            </FormControl>
            <FormMessage footnote={props.footnote} disabled={props.disabled} />
          </FormItem>
        )
      }}
    />
  )
}

function FormFieldSwitch<V extends FieldValues = FieldValues>(
  props: FieldFormProps<V>,
) {
  return (
    <FormField
      control={props.control}
      name={props.name}
      required={props.required}
      disabled={props.disabled}
      render={({ field }) => {
        return (
          <Switch
            checked={field.value}
            onCheckedChange={field.onChange}
            disabled={props.disabled}
          />
        )
      }}
    />
  )
}

function FormFieldRadioGroup<V extends FieldValues = FieldValues>({
  orientation = 'row',
  ...props
}: CheckboxFieldProps<V> & { orientation?: 'row' | 'column' }) {
  return (
    <FormField
      control={props.control}
      name={props.name}
      required={props.required}
      disabled={props.disabled}
      render={({ field }) => {
        return (
          <FormItem>
            <div className="group relative flex flex-col gap-2">
              <Label required={props.required}>{props.label}</Label>
              <FormControl className="grid items-stretch">
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className={cn('user-select-none flex flex-wrap gap-2', {
                    'flex-col': orientation === 'column',
                  })}
                  disabled={props.disabled || props.readonly}
                >
                  {props.options.map((opt) => (
                    <FormItem key={opt.value}>
                      <label
                        className={cn(
                          'bg-input-secondary-background hover:bg-input-background flex min-h-[39px] cursor-pointer items-center space-y-0 space-x-3 rounded-md px-3 py-3',
                          {
                            'bg-input-background':
                              opt.value === field.value && !props.disabled,
                            'pointer-events-none': props.disabled,
                            'py-1': !!opt.description && orientation === 'row',
                          },
                        )}
                      >
                        <FormControl>
                          <RadioGroupItem
                            value={opt.value}
                            disabled={props.disabled}
                          />
                        </FormControl>
                        <div
                          className={cn(
                            'text-input-foreground flex w-full cursor-pointer font-normal',
                            { 'flex-col': orientation === 'column' },
                          )}
                        >
                          <FormLabel className="text-md text-input-foreground flex-1 cursor-pointer font-medium">
                            {opt.label}
                          </FormLabel>
                          {!!opt.description && (
                            <FormLabel className="text-input-secondary-foreground cursor-pointer text-sm font-normal">
                              {opt.description}
                            </FormLabel>
                          )}
                        </div>
                      </label>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormControl>
            </div>
            <FormMessage footnote={props.footnote} disabled={props.disabled} />
          </FormItem>
        )
      }}
    />
  )
}

function FormErrorAlert() {
  const {
    formState: { errors },
  } = useFormContext()
  const message = errors.root?.message
  const instance = React.useRef<null | string | number>(null)
  React.useEffect(() => {
    if (message) {
      instance.current = toast(
        <div className="w-full text-center">{message}</div>,
        {
          position: 'top-center',
          duration: 60000,
          classNames: {
            toast:
              'group-[.toaster]:bg-warn group-[.toaster]:text-center group-[.toaster]:border-0 group-[.toaster]:text-white',
            title: 'group-[.toast]:text-center',
          },
        },
      )
    } else if (instance.current && !message) {
      toast.dismiss(instance.current)
      instance.current = null
    }

    return () => {
      if (instance.current) toast.dismiss(instance.current)
    }
  }, [message])

  return null
}

interface FormFooterControlProps {
  cancelLabel?: string
  submitLabel?: string
  onReset: () => void
}

function FormFooterControl(props: FormFooterControlProps) {
  const { formState } = useFormContext()

  return (
    <AnimatePresence>
      {formState.isDirty && (
        <m.div
          className="flex justify-end gap-3"
          initial={{ overflow: 'hidden', height: 0, opacity: 0, scaleY: 0.0 }}
          animate={{
            overflow: 'hidden',
            height: 'auto',
            opacity: 1,
            scaleY: 1,
          }}
          exit={{ overflow: 'hidden', height: 0, opacity: 0, scaleY: 0.0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <Button
            variant="outline"
            onClick={props.onReset}
            disabled={formState.isLoading}
          >
            {props.cancelLabel || 'Cancel'}
          </Button>
          <Button type="submit" loading={formState.isLoading}>
            {props.submitLabel || 'Save Changes'}
          </Button>
        </m.div>
      )}
    </AnimatePresence>
  )
}

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
  FormFieldInput,
  FormFieldSlider,
  FormFieldCheckboxGroup,
  FormFieldRadioGroup,
  FormFieldMultiline,
  FormFieldPhoneInput,
  FormErrorAlert,
  FormFieldMultivalue,
  FormFieldSelect,
  FormFieldSwitch,
  FieldFormProps,
  FormFooterControl,
  FormFieldDuration,
  FormFieldPercent,
}
